import type { CallbackEvent } from "@shopify/polaris-types";
import { stripShopifyGid, toShopifyProductGid } from "@codway-dev/shared";
import type { TriggerProduct } from "../../../store/triggers.mapping";
import {
  CollapsibleSectionCard,
  SectionHeader,
  ProductPickerButton,
} from "../../../components/shared/EditorShared";

// ─── Types ───────────────────────────────────────────────────────────────────

export type OneTickTriggerType = "all" | "specific_products";

// ─── Picker helper ───────────────────────────────────────────────────────────

async function openTriggerProductsPicker(
  currentProducts: TriggerProduct[],
  onSelect: (products: TriggerProduct[]) => void
) {
  try {
    const selected = await window.shopify?.resourcePicker({
      type: "product",
      multiple: true,
      // Reconstitute the GID — `resourcePicker` expects the full GID
      // format for `selectionIds` to round-trip pre-selection.
      selectionIds: currentProducts.map((p) => ({
        id: toShopifyProductGid(p.id),
      })),
    });
    if (!selected) return;
    onSelect(
      selected.map((p) => ({
        id: stripShopifyGid(p.id),
        title: p.title,
        imageUrl: p.images[0]?.originalSrc,
      }))
    );
  } catch {
    window.shopify?.toast?.show("No se pudo abrir el selector de productos.", {
      isError: true,
    });
  }
}

// ─── Section 1: Name ─────────────────────────────────────────────────────────

export function NameSection({
  value,
  onChange,
}: {
  value: string;
  onChange: (v: string) => void;
}) {
  return (
    <CollapsibleSectionCard
      header={<SectionHeader number={1} title="Configura tu upsell" />}
    >
      <s-stack direction="block" gap="base">
        <s-text-field
          label="Nombre del upsell"
          value={value}
          onInput={(e: CallbackEvent<"s-text-field">) =>
            onChange(e.currentTarget.value)
          }
        />
      </s-stack>
    </CollapsibleSectionCard>
  );
}

// ─── Section 2: Trigger ──────────────────────────────────────────────────────

const TRIGGER_OPTIONS: { value: OneTickTriggerType; label: string }[] = [
  { value: "all", label: "Todos los productos" },
  { value: "specific_products", label: "Productos específicos" },
];

export function TriggerSection({
  value,
  onChange,
  products,
  onProductsChange,
}: {
  value: OneTickTriggerType;
  onChange: (t: OneTickTriggerType) => void;
  products: TriggerProduct[];
  onProductsChange: (products: TriggerProduct[]) => void;
}) {
  const removeProduct = (id: string) =>
    onProductsChange(products.filter((p) => p.id !== id));

  return (
    <CollapsibleSectionCard
      header={<s-text type="strong">Mostrar el upsell para:</s-text>}
    >
      <s-stack direction="block" gap="base">
        <div className="flex bg-gray-100 p-1 rounded-lg">
          {TRIGGER_OPTIONS.map((opt) => (
            <button
              key={opt.value}
              onClick={() => onChange(opt.value)}
              className={`flex-1 py-2 text-sm font-medium rounded-md transition-all ${
                value === opt.value
                  ? "bg-white text-gray-900 shadow-sm"
                  : "text-gray-500 hover:text-gray-700"
              }`}
            >
              {opt.label}
            </button>
          ))}
        </div>

        {value === "specific_products" && (
          <s-stack direction="block" gap="small-200">
            <ProductPickerButton
              label={
                products.length > 0
                  ? `${products.length} producto(s) seleccionado(s)`
                  : "Seleccionar productos"
              }
              onClick={() =>
                openTriggerProductsPicker(products, onProductsChange)
              }
            />

            {products.map((p) => (
              <div
                key={p.id}
                className="flex items-center gap-3 p-3 rounded-lg border border-gray-200 bg-gray-50"
              >
                {p.imageUrl ? (
                  <img
                    src={p.imageUrl}
                    alt={p.title}
                    className="w-10 h-10 rounded object-cover flex-shrink-0"
                  />
                ) : (
                  <div className="w-10 h-10 rounded bg-gray-200 flex items-center justify-center flex-shrink-0">
                    <s-icon type="image" />
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-900 truncate">
                    {p.title}
                  </p>
                </div>
                <button
                  onClick={() => removeProduct(p.id)}
                  className="text-red-500 hover:text-red-700 flex-shrink-0"
                  aria-label="Quitar producto"
                >
                  <s-icon type="delete" />
                </button>
              </div>
            ))}
          </s-stack>
        )}
      </s-stack>
    </CollapsibleSectionCard>
  );
}
